import {
  Box,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
} from '@chakra-ui/react'
import React, { useEffect, useState } from 'react'
import { Column, PluginHook, TableInstance, useTable } from 'react-table'
import { IFilter } from './TableAbstractSCN'

export const CustomTable = <T extends object>(
  props: React.PropsWithChildren<{
    customUseTable?: TableInstance<T>
    sort?: IFilter[]
    keepLastRowClicked?: boolean
    onClickRow?: (row: T) => void
    onHoverRowIn?: (row: T) => void
    onHoverRowOut?: () => void
    columns: Column<T>[]
    data: T[]
    orientation?: 'left' | 'center' | 'right'
    noHover?: boolean
    onClickHeader?: (headerId: string) => void
    clickableHeaders?: string[]
    hooks?: PluginHook<T>[]
    setSelectedFlatRows?: (selectedFlatRows: T[]) => void
    initialSelectedFlatRows?: T[]
    isFetching?: boolean
  }>,
) => {
  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow,
    selectedFlatRows,
  } =
    //@ts-ignore
    props?.customUseTable ||
    useTable(
      {
        columns: props.columns,
        data: props.data,
        initialState: {
          selectedRowIds: (props.initialSelectedFlatRows || []).map(
            (row) => row.rowId,
          ),
        },
      },
      ...(props.hooks || []),
    )

  useEffect(() => {
    if (props.setSelectedFlatRows) {
      props.setSelectedFlatRows(
        (selectedFlatRows || []).map((row) => ({
          id: row.original.id,
          rowId: row.id,
        })),
      )
    }
  }, [selectedFlatRows])

  const [rowClicked, setRowClicked] = useState<number>()

  const findSortFilter = (headerId: string) =>
    props.sort?.find((s) => s.column === headerId && s.type === 'sort')

  return (
    <TableContainer
      w='full'
      h='full'
      borderBottom='1px'
      borderColor='gray.100'
      overflowX={'auto'}
      overflowY={'auto'}
      sx={{
        maxHeight: `calc(100vh - 345px)`,
      }}
    >
      <Table {...getTableProps()} size='md'>
        <Thead>
          {
            // Loop over the header rows
            headerGroups.map((headerGroup) => (
              // Apply the header row props
              <Tr {...headerGroup.getHeaderGroupProps()}>
                {headerGroup.headers.map((column) => {
                  const sortFilter = findSortFilter(column.id)
                  const isClickable =
                    props.onClickHeader &&
                    (props.clickableHeaders || [])?.includes(column.id)

                  return (
                    <Th
                      cursor={isClickable ? 'pointer' : 'default'}
                      align={props.orientation}
                      scope='col'
                      py={3}
                      position='sticky'
                      top={0}
                      bg='white'
                      zIndex={['selection'].includes(column.id) ? 1 : 0}
                      onClick={() => {
                        if (isClickable && !props.isFetching) {
                          props.onClickHeader?.(column.id)
                        }
                      }}
                      {...column.getHeaderProps()}
                    >
                      {
                        // Render the header
                        column.render('Header')
                      }
                      <span>
                        {sortFilter
                          ? sortFilter.value === 'DESC'
                            ? ' ▼'
                            : ' ▲'
                          : ''}
                      </span>
                    </Th>
                  )
                })}
              </Tr>
            ))
          }
        </Thead>
        {/* Apply the table body props */}
        <Tbody {...getTableBodyProps()}>
          {
            // Loop over the table rows
            rows.map((row) => {
              // Prepare the row for display
              prepareRow(row)
              return (
                // Apply the row props
                <Tr
                  {...row.getRowProps()}
                  onMouseEnter={() => {
                    if (props.onHoverRowIn) {
                      props.onHoverRowIn(row.original)
                    }
                  }}
                  onMouseLeave={() => {
                    if (props.onHoverRowOut) {
                      props.onHoverRowOut()
                    }
                  }}
                  onClick={() => {
                    if (props.onClickRow) {
                      setRowClicked(row.index)
                      props.onClickRow(row.original)
                    }
                  }}
                  px={3}
                  py={3}
                  fontSize='xs'
                  color='gray.500'
                  bg={rowClicked === row.index ? 'gray.50' : 'transparent'}
                  borderLeft='2px'
                  borderColor='transparent'
                  _hover={
                    props.noHover
                      ? {}
                      : {
                          bg: 'gray.50',
                          cursor: 'pointer',
                          color: 'gray.600',
                          borderLeft: '2px',
                          borderColor: 'brand.500',
                        }
                  }
                >
                  {
                    // Loop over the rows cells
                    row.cells.map((cell, index) => {
                      if (index === 0)
                        return (
                          <Td
                            {...cell.getCellProps()}
                            style={cell.column.styles}
                            fontWeight={500}
                            fontSize='xs'
                          >
                            {
                              // Render the cell contents
                              cell.render('Cell')
                            }
                          </Td>
                        )
                      return (
                        <Td
                          fontWeight='400'
                          fontSize='xs'
                          style={cell.column.styles}
                          {...cell.getCellProps()}
                        >
                          {
                            // Render the cell contents
                            cell.render('Cell')
                          }
                        </Td>
                      )
                    })
                  }
                </Tr>
              )
            })
          }
        </Tbody>
      </Table>
      {!rows?.length && (
        <Box p={4}>
          <Text fontSize='sm' color='gray.600' textAlign='center'>
            Ningún registro hasta el momento
          </Text>
        </Box>
      )}
    </TableContainer>
  )
}
